import React, {useEffect, useState} from 'react';
import {Typography} from "@mui/material";
import './DonutChart.scss';
import {SkeletonComponent} from "@syncfusion/ej2-react-notifications";
import {PieChart} from "@mui/x-charts/PieChart";
import {HighlightItemData} from '@mui/x-charts/context'
import {PieItemIdentifier} from "@mui/x-charts";

export interface DonutData {
    status: string;
    count: number;
    color: string;
    prediction: string;
}
export interface DonutChartProps {
    data: DonutData[]
    chartWidth?: number | undefined
    chartHeight?: number | undefined
    isLoading?: boolean | undefined
    showLegend?: boolean | undefined
    onSegmentClick?: (event: any) => void
}

export function DonutChart(props: DonutChartProps) {
    const { data, isLoading, chartWidth, chartHeight, onSegmentClick, showLegend } = props;
    const [highlightedItem, setHighlightedItem] = useState<HighlightItemData | null>(null);
    const [selectedSegment, setSelectedSegment] = useState<PieItemIdentifier | null>(null);
    const donutWidth = chartWidth || 200;
    const donutHeight = chartHeight || 200
    const donutData = data
        .map(item => ({ category: item.status, value: item.count, color: item.color, prediction: item.prediction }));    const total = data.reduce((sum, item) => sum + item.count, 0);
    
    const handleSegmentClick = (pieItem: PieItemIdentifier) => {
        if(selectedSegment?.dataIndex == pieItem.dataIndex) {
            setSelectedSegment(null);
            setHighlightedItem(null);
        } else {
            setSelectedSegment(pieItem);
            setHighlightedItem({
                seriesId: pieItem.seriesId,
                dataIndex: pieItem.dataIndex
            });
        }
        onSegmentClick?.(donutData[pieItem.dataIndex]);
    }
    
    const handleLegendClick = (index: number) => {
        if(selectedSegment?.dataIndex == index) {
            setSelectedSegment(null);
            setHighlightedItem(null);
        } else {
            setSelectedSegment({seriesId: 'pie-data', dataIndex: index} as PieItemIdentifier);
            setHighlightedItem({seriesId: 'pie-data', dataIndex: index} as HighlightItemData);
        }
        
        onSegmentClick?.(donutData[index]);
    }
    
    const handleHighlightChange = (highlightedItem: HighlightItemData | null) => {
        if(selectedSegment == null) {
            setHighlightedItem(highlightedItem);
        }
    }
    
    return (
        <div className='e-card dashboard-card packing-status'>
            <div className='pie-chart-container' style={showLegend ? { maxWidth: '50%' } : {}} >
                {isLoading
                    ? <SkeletonComponent shape={'Circle'} width={donutWidth} height={donutHeight}/>
                    : <PieChart
                        series={[
                            {
                                id:'pie-data',
                                data: donutData.map((item) => ({
                                    label: item.category,
                                    value: item.value,
                                    color: item.color,
                                })),
                                innerRadius: 70,
                                outerRadius: 100,
                                startAngle: -120,
                                paddingAngle: 3,
                                cornerRadius: 3,
                                highlightScope: {fade: 'series', highlight: 'item'},
                                faded: {outerRadius: 100}
                            },
                        ]}
                        width={donutWidth}
                        height={donutHeight}
                        margin={{right: 0}}
                        slotProps={{
                            legend: {hidden: true},
                        }}
                        onItemClick={(event, d) => handleSegmentClick(d)}
                        highlightedItem={highlightedItem}
                        onHighlightChange={handleHighlightChange}
                    />
                }
            </div>
            { showLegend ?
                    <div className='summary-container'>
                        {isLoading
                            ? <div style={{ padding: '0 5rem'}}>
                                <SkeletonComponent shape={'Rectangle'} width={'100%'} height='2em'/>
                                <SkeletonComponent shape={'Rectangle'} width={'100%'} height='2em'/>
                                <SkeletonComponent shape={'Rectangle'} width={'100%'} height='2em'/>
                                <SkeletonComponent shape={'Rectangle'} width={'100%'} height='2em'/>
                                <SkeletonComponent shape={'Rectangle'} width={'100%'} height='2em'/>
                                <div className='summary-skeleton-separator'/>
                                <SkeletonComponent shape={'Rectangle'} width={'100%'} height='2em'/>
                            </div>
                            : <div className='status-details'>
                                {data.map((entry, index) => (
                                    <div key={index} className='status-entry' onClick={() => handleLegendClick(index)} style={{cursor: 'pointer'}}>
                                        <div className='status-label'>
                                            <div className='status-color' style={{backgroundColor: entry.color}}/>
                                            <Typography className='status'>{entry.status}</Typography>
                                        </div>
                                        <Typography fontSize={'small'} fontWeight={'bold'}>{entry.count}</Typography>
                                    </div>
                                ))}
                                <div className='status-total'>
                                    <div className='total-label'>
                                        <Typography fontWeight={'bold'}>Total</Typography>
                                        <Typography fontWeight={'bold'}>{total}</Typography>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                :
                    ""
            }
        </div>
    );
}